import { PDFPageTree, PDFName, PDFDocument } from 'pdf-lib'
import FormatTextUtil from './FormatText.util'
import { ConvertDocumentService, LocalStorageService } from '../services'

/**
 * @author Pedro Antonio
 * @export
 * @class PDFUtil
 */
export default class PDFUtil {

    static #convertDocumentService = new ConvertDocumentService(process.env.REACT_APP_MIFIRMA_API)

    /**
    * @author Pedro Antonio
    * @description Validates if there's an error while get pages
    * @param {import('pdf-lib').PDFDocument} PDFDocument
    * @returns {{ hadError: boolean, PDFDocument: import('pdf-lib').PDFDocument }}
    */
    static fixPageType = (PDFDocument) => {
        let data = {
            hadError: null,
            PDFDocument
        }
        let error
        let canBePagged
        try {
            canBePagged = PDFDocument.getPageCount()
            if (canBePagged) return { ...data, hadError: null, PDFDocument }
        } catch (e) {
            error = e
        }

        if (error && !canBePagged) {
            try {
                // Find reference to the page tree
                const pagesRef = PDFDocument.catalog.get(PDFName.of('Pages'))

                // Get the page tree. This is a PDFDict.
                const oldPageTree = PDFDocument.context.indirectObjects.get(pagesRef)

                // Create a PDFPageTree with the same content.
                const newPageTree = new PDFPageTree(oldPageTree.dict, oldPageTree.context)

                // Set the correct `Type`.
                newPageTree.dict.set(PDFName.of('Type'), PDFName.of('Pages'));

                // Replace the PDFDict with the PDFPageTree in the document.
                PDFDocument.context.indirectObjects.set(pagesRef, newPageTree)

                return { ...data, hadError: error, PDFDocument }
            } catch (e) {
                return { ...data, hadError: e, PDFDocument: null }
            }
        }
    }

    /**
    * @author Pedro Antonio
    * @description Validates if there's an error while get pages
    * @param {import('pdf-lib').PDFDocument} PDFDocument
    */
    static getAndValidatePDFPages = (PDFDocument) => {
        let error

        try {
            return PDFDocument.getPageCount()
        } catch (e) {
            error = e
        }

        if (error) {
            try {
                // Find reference to the page tree
                const pagesRef = PDFDocument.catalog.get(PDFName.of('Pages'))

                // Get the page tree. This is a PDFDict.
                const oldPageTree = PDFDocument.context.indirectObjects.get(pagesRef)

                // Create a PDFPageTree with the same content.
                const newPageTree = new PDFPageTree(oldPageTree.dict, oldPageTree.context)

                // Set the correct `Type`.
                newPageTree.dict.set(PDFName.of('Type'), PDFName.of('Pages'));

                // Replace the PDFDict with the PDFPageTree in the document.
                PDFDocument.context.indirectObjects.set(pagesRef, newPageTree)

                return PDFDocument.getPageCount()
            } catch (e) {
                return 0
            }
        }
    }

    /**
     *
     *
     * @static
     * @param {import('pdf-lib').PDFDocument} PDFDocument
     * @memberof PDFUtil
     */
    static getAndValidatePagesInPDF(PDFDocument) {
        let error

        try {
            return PDFDocument.getPages()
        } catch (e) {
            error = e
        }

        if (error) {
            try {
                // Find reference to the page tree
                const pagesRef = PDFDocument.catalog.get(PDFName.of('Pages'))

                // Get the page tree. This is a PDFDict.
                const oldPageTree = PDFDocument.context.indirectObjects.get(pagesRef)

                // Create a PDFPageTree with the same content.
                const newPageTree = new PDFPageTree(oldPageTree.dict, oldPageTree.context)

                // Set the correct `Type`.
                newPageTree.dict.set(PDFName.of('Type'), PDFName.of('Pages'));

                // Replace the PDFDict with the PDFPageTree in the document.
                PDFDocument.context.indirectObjects.set(pagesRef, newPageTree)

                return PDFDocument.getPages()
            } catch (e) {
                return []
            }
        }
    }

    /**
     * @static
     * @param {File | Blob} file
     * @returns {Promise<string>}
     * @memberof PDFUtil
     */
    static baseTo64Pdf = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }

    /**
     * @typedef {Object} getPdfBase64Params
     * @property {string} fileName
     * @property {string} fileExtension
     * @property {string} base64
     * @property {boolean} [error]
     * @property {string} [errorMessage]
     */

    /**
     * @static
     * @description Get pdf's file base64 string, using a File instance
     * @param {File} pdfFile PDF File
     * @param {boolean} [withoutÑ] Normalize text without Ñ
     * @returns {Promise<getPdfBase64Params>}
     * @memberof PDFUtil
     */
    static getPdfBase64 = async (pdfFile, withoutÑ = true) => {
        const token = LocalStorageService.retrieveToken()
        let response = {
            fileName: "",
            fileExtension: "",
            base64: "",
            pdfBase64: "",
            error: false,
            errorMessage: ""
        }
        try {
            if (pdfFile instanceof File) {
                let fileName = withoutÑ ? FormatTextUtil.normalizeTextExcludedÑ(pdfFile.name) : FormatTextUtil.normalizeTextIncludedÑ(pdfFile.name)

                const validExtension = fileName.split(".").pop().toLowerCase() === "pdf"
                if (!validExtension) throw new Error("errors.isNotAPdf")

                const newFile = new File([pdfFile], fileName, {
                    type: "application/pdf"
                })

                let fileBase64 = await this.baseTo64Pdf(newFile);

                let pdfDoc = await PDFDocument.load(fileBase64);

                const { hadError, PDFDocument: pdfReturned } = this.fixPageType(pdfDoc)
                pdfDoc = pdfReturned
                if (hadError) {
                    fileBase64 = await pdfDoc.saveAsBase64()
                }

                const author = pdfDoc.getAuthor()
                const creator = pdfDoc.getCreator()
                const pages = pdfDoc.getPages()

                if (author === "" && creator === "" && pages <= 35) {
                    let data = new FormData();
                    data.append("doc", newFile);
                    let pdfConverted = await this.#convertDocumentService.convertDocumentPDF(token, data)
                    const file = new Blob([new Uint8Array(pdfConverted.data).buffer], { type: 'application/pdf;base64' });
                    fileBase64 = await this.baseTo64Pdf(file);
                }

                let pdfBase64 = fileBase64.split(",")[1];

                response = {
                    ...response,
                    fileName,
                    pdfBase64,
                    base64: fileBase64
                }
                return response
            } else throw new Error("errors.isNotAFile")
        } catch (e) {
            response = {
                ...response,
                error: true,
                errorMessage: e.message
            }
            return response
        }
    }
}